import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

/**
 * @class InputFile
 * @extends NeptuneElement
 *
 * File picker component. Renders an accessible label, an upload button, a hidden
 * native `<input type="file">`, and a file-list region built from
 * `neptune-input-file-item` elements. Owns file selection, extension/MIME/size
 * validation, and list management; delegates per-item progress/success/error
 * rendering to `neptune-input-file-item` — those states are supplied by the
 * consuming application via `updateFileState()`.
 */
class InputFile extends i18nMixin(NeptuneElement) {

    static DEFAULT_ICON = 'fa-solid fa-upload';

    templateMarkup = `
        <div>
            <slot class="font-size-md" name="label"></slot>
            <neptune-button id="uploadButton" type="primary" size="small" icon-start="fa-solid fa-upload">
                <span id="buttonText">Choose a file</span>
            </neptune-button>
            <input id="nativeInput" type="file" hidden tabindex="-1" aria-hidden="true" />
            <div id="fileList" aria-live="polite" data-state="empty">
                <span class="font-size-sm" id="emptyState">No file chosen</span>
            </div>
        </div>
    `;

    componentCssUrl = '/src/components/form/inputFile/input-file.css';

    #files = [];
    #uploadButtonElement = null;
    #nativeInputElement = null;
    #fileListElement = null;
    #handleUploadButtonClick = null;
    #handleNativeInputChange = null;
    #handleItemRemove = null;

    constructor() {
        super();
    }

    /**
     * Invoked when the component is added to the DOM.
     * Processes pre-set attributes and renders the initial button/list state.
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(InputFile.observedAttributes);
        this.#updateButtonText();
        this.#syncEmptyState();
        this.#updateEmptyStateText();
        this.setEventListeners();
    }

    /**
     * Invoked when the component is removed from the DOM.
     * Cleans up all event listeners.
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['multiple', 'disabled', 'accept', 'max-file-size', 'culture'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) { return; }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'culture':
                if (newValue) {
                    i18n.setLocale(newValue);
                    this.onLocaleChange();
                }
                break;
            case 'multiple':
                if (this.hasAttribute('multiple')) { this.#nativeInputElement?.setAttribute('multiple', ''); }
                else { this.#nativeInputElement?.removeAttribute('multiple'); }
                break;
            case 'accept':
                if (newValue) { this.#nativeInputElement?.setAttribute('accept', newValue); }
                else { this.#nativeInputElement?.removeAttribute('accept'); }
                break;
            case 'disabled': {
                const isDisabled = this.hasAttribute('disabled');
                if (isDisabled) { this.#uploadButtonElement?.setAttribute('disabled', ''); }
                else { this.#uploadButtonElement?.removeAttribute('disabled'); }
                if (this.#nativeInputElement) { this.#nativeInputElement.disabled = isDisabled; }
                this.#syncDisabledState();
                break;
            }
            case 'max-file-size':
            default:
                break;
        }
    }

    setEventListeners() {
        if (!this.isRendered) { return; }
        this.removeEventListeners();

        this.#uploadButtonElement = this.shadowRoot?.querySelector('#uploadButton');
        this.#nativeInputElement = this.shadowRoot?.querySelector('#nativeInput');
        this.#fileListElement = this.shadowRoot?.querySelector('#fileList');

        this.#handleUploadButtonClick = () => {
            if (this.disabled) { return; }
            this.#nativeInputElement?.click();
        };
        this.#uploadButtonElement?.addEventListener('click', this.#handleUploadButtonClick);

        this.#handleNativeInputChange = () => { this.#processSelectedFiles(); };
        this.#nativeInputElement?.addEventListener('change', this.#handleNativeInputChange);

        this.#handleItemRemove = (event) => { this.#removeFile(event.detail); };
        this.#fileListElement?.addEventListener('file-remove', this.#handleItemRemove);
    }

    removeEventListeners() {
        if (this.#uploadButtonElement && this.#handleUploadButtonClick) {
            this.#uploadButtonElement.removeEventListener('click', this.#handleUploadButtonClick);
        }
        if (this.#nativeInputElement && this.#handleNativeInputChange) {
            this.#nativeInputElement.removeEventListener('change', this.#handleNativeInputChange);
        }
        if (this.#fileListElement && this.#handleItemRemove) {
            this.#fileListElement.removeEventListener('file-remove', this.#handleItemRemove);
        }
        this.#uploadButtonElement = null;
        this.#nativeInputElement = null;
        this.#fileListElement = null;
        this.#handleUploadButtonClick = null;
        this.#handleNativeInputChange = null;
        this.#handleItemRemove = null;
    }

    get multiple() { return this.hasAttribute('multiple'); }
    set multiple(value) {
        if (value) { this.setAttribute('multiple', ''); }
        else { this.removeAttribute('multiple'); }
    }

    get disabled() { return this.hasAttribute('disabled'); }
    set disabled(value) {
        if (value) { this.setAttribute('disabled', ''); }
        else { this.removeAttribute('disabled'); }
    }

    get accept() { return this.getAttribute('accept') || ''; }
    set accept(value) {
        if (value) { this.setAttribute('accept', String(value)); }
        else { this.removeAttribute('accept'); }
    }

    get maxFileSize() {
        const numeric = Number(this.getAttribute('max-file-size'));
        return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    }

    set maxFileSize(value) {
        if (value) { this.setAttribute('max-file-size', String(value)); }
        else { this.removeAttribute('max-file-size'); }
    }

    /** @returns {File[]} The currently listed files, in display order. */
    get files() { return this.#files.map(entry => entry.file); }

    /**
     * Applies progress/status updates supplied by the consuming application to an
     * existing file item. Unspecified fields are left unchanged, except that changing
     * `status` clears any previous statusMessage/errorMessage so stale text from the
     * old status can't linger, unless a new one is supplied in the same call.
     * @param {string} id
     * @param {{status?: string, progress?: number, statusMessage?: string, errorMessage?: string}} patch
     * @returns {void}
     */
    updateFileState(id, { status, progress, statusMessage, errorMessage } = {}) {
        const entry = this.#files.find(item => item.id === id);
        if (!entry) { return; }

        if (status !== undefined && status !== entry.status) {
            entry.statusMessage = '';
            entry.errorMessage = '';
            entry.errorKey = null;
        }

        if (status !== undefined) { entry.status = status; }
        if (progress !== undefined) { entry.progress = progress; }
        if (statusMessage !== undefined) { entry.statusMessage = statusMessage; }
        if (errorMessage !== undefined) {
            entry.errorMessage = errorMessage;
            entry.errorKey = null;
        }

        const itemElement = this.#fileListElement?.querySelector(`neptune-input-file-item[item-id="${id}"]`);
        if (!itemElement) { return; }

        itemElement.setAttribute('status', entry.status);
        itemElement.setAttribute('progress', String(entry.progress));

        if (entry.statusMessage) { itemElement.setAttribute('status-message', entry.statusMessage); }
        else { itemElement.removeAttribute('status-message'); }

        if (entry.errorMessage) { itemElement.setAttribute('error-message', entry.errorMessage); }
        else { itemElement.removeAttribute('error-message'); }
    }

    /**
     * Checks whether a file matches one already selected (by name, size, and last
     * modified time), either already listed or earlier in the same selection batch.
     * @param {File} file
     * @param {File[]} queuedFiles
     * @returns {boolean}
     */
    #isDuplicateFile(file, queuedFiles) {
        const matches = (other) => other.name === file.name && other.size === file.size && other.lastModified === file.lastModified;
        return this.#files.some(entry => matches(entry.file)) || queuedFiles.some(matches);
    }

    /**
     * Validates a single file against configured extensions, MIME types, and size.
     * @param {File} file
     * @returns {{key: string, params?: Object}|null} A translation descriptor, or null when the file is valid.
     */
    #validateFile(file) {
        const maxFileSize = this.maxFileSize;
        if (maxFileSize && file.size > maxFileSize) {
            return { key: 'INPUT_FILE.VALIDATION.EXCEEDS_LIMIT', params: { size: this.#formatFileSize(maxFileSize) } };
        }

        return null;
    }

    /**
     * Resolves a validation descriptor into a localised message for the active locale,
     * substituting any `{token}` placeholders declared in the locale file.
     * @param {{key: string, params?: Object}|null} error
     * @returns {string}
     */
    #resolveErrorMessage(error) {
        if (!error?.key) { return ''; }

        const message = this.translate(error.key);
        return Object.entries(error.params || {}).reduce(
            (text, [token, value]) => text.replaceAll(`{${token}}`, value),
            message
        );
    }

    /**
     * Formats a byte count as a human-readable size string.
     * @param {number} bytes
     * @returns {string}
     */
    #formatFileSize(bytes) {
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let value = bytes;
        let unitIndex = 0;
        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024;
            unitIndex += 1;
        }

        const precision = unitIndex === 0 ? 0 : 1;
        return `${value.toFixed(precision)} ${units[unitIndex]}`;
    }

    /**
     * Reads the native input's current FileList, validates each file, replaces the
     * existing selection when not `multiple`, re-renders, and emits `file-selected`
     * with only the files that passed validation.
     * @returns {void}
     */
    #processSelectedFiles() {
        const selectedFiles = Array.from(this.#nativeInputElement?.files || []);
        if (selectedFiles.length === 0) { return; }

        if (!this.multiple) {
            this.#files = [];
            this.#clearFileItemElements();
        }

        const queuedFiles = [];
        const newEntries = [];
        const validFiles = [];
        selectedFiles.forEach(file => {
            const error = this.#isDuplicateFile(file, queuedFiles)
                ? { key: 'INPUT_FILE.VALIDATION.DUPLICATE' }
                : this.#validateFile(file);
            queuedFiles.push(file);

            const entry = {
                id: `input-file-item-${Math.random().toString(36).slice(2, 8)}`,
                file,
                status: error ? 'error' : 'in-progress',
                progress: 0,
                statusMessage: '',
                errorKey: error,
                errorMessage: this.#resolveErrorMessage(error)
            };
            this.#files.push(entry);
            newEntries.push(entry);
            if (!error) { validFiles.push(file); }
        });

        this.#syncEmptyState();
        this.#appendFileItemElements(newEntries);
        this.#updateButtonText();

        if (this.#nativeInputElement) { this.#nativeInputElement.value = ''; }

        this.dispatchEvent(new CustomEvent('file-selected', {
            detail: { files: validFiles },
            bubbles: true,
            composed: true
        }));
    }

    /**
     * Removes a file entry by id, re-renders, and emits `file-removed`.
     * @param {{id?: string, file?: File}} detail
     * @returns {void}
     */
    #removeFile(detail) {
        const index = this.#files.findIndex(entry => entry.id === detail?.id);
        if (index === -1) { return; }

        const [removed] = this.#files.splice(index, 1);
        this.#removeFileItemElement(removed.id);
        this.#syncEmptyState();
        this.#updateButtonText();
        if (this.#nativeInputElement) { this.#nativeInputElement.value = ''; }

        this.dispatchEvent(new CustomEvent('file-removed', {
            detail: { file: removed.file },
            bubbles: true,
            composed: true
        }));
    }

    /**
     * Removes only the DOM element for one file, leaving all other rendered items untouched.
     * @param {string} id
     * @returns {void}
     */
    #removeFileItemElement(id) {
        this.#fileListElement?.querySelector(`neptune-input-file-item[item-id="${id}"]`)?.remove();
    }

    /**
     * Removes all rendered file items without touching the empty-state element.
     * @returns {void}
     */
    #clearFileItemElements() {
        this.#fileListElement?.querySelectorAll('neptune-input-file-item').forEach(item => item.remove());
    }

    /**
     * Appends a `neptune-input-file-item` for each newly added entry, leaving existing items untouched.
     * @param {Array<object>} entries
     * @returns {void}
     */
    #appendFileItemElements(entries) {
        if (!this.#fileListElement) { return; }

        entries.forEach(entry => {
            const itemElement = document.createElement('neptune-input-file-item');
            itemElement.setAttribute('item-id', entry.id);
            itemElement.setAttribute('status', entry.status);
            itemElement.setAttribute('removable', '');
            itemElement.setAttribute('progress', String(entry.progress));
            if (entry.statusMessage) { itemElement.setAttribute('status-message', entry.statusMessage); }
            if (entry.errorMessage) { itemElement.setAttribute('error-message', entry.errorMessage); }
            if (this.disabled) { itemElement.setAttribute('disabled', ''); }
            itemElement.file = entry.file;
            this.#fileListElement.appendChild(itemElement);
        });
    }

    /**
     * Toggles the empty-state placeholder without touching any rendered file items.
     * @returns {void}
     */
    #syncEmptyState() {
        if (!this.#fileListElement) { return; }

        this.#fileListElement.dataset.state = this.#files.length > 0 ? 'has-files' : 'empty';

        const hasEmptyState = !!this.#fileListElement.querySelector('#emptyState');
        if (this.#files.length === 0 && !hasEmptyState) {
            const emptyState = document.createElement('span');
            emptyState.id = 'emptyState';
            emptyState.className = 'font-size-sm';
            emptyState.textContent = this.translate('INPUT_FILE.EMPTY_STATE');
            this.#fileListElement.appendChild(emptyState);
        } else if (this.#files.length > 0 && hasEmptyState) {
            this.#fileListElement.querySelector('#emptyState')?.remove();
        }
    }

    /**
     * Syncs the disabled attribute onto every rendered file item in place.
     * @returns {void}
     */
    #syncDisabledState() {
        const isDisabled = this.disabled;
        this.#fileListElement?.querySelectorAll('neptune-input-file-item').forEach(item => {
            if (isDisabled) { item.setAttribute('disabled', ''); }
            else { item.removeAttribute('disabled'); }
        });
    }

    /**
     * Updates the button-text slot's fallback content based on whether any files
     * are selected. Leaves a consumer-supplied slot untouched.
     * @returns {void}
     */
    #updateButtonText() {
        const buttonText = this.shadowRoot?.querySelector('#buttonText');
        if (!buttonText) { return; }

        buttonText.textContent = this.#files.length > 0
            ? this.translate('INPUT_FILE.BUTTON.CHOOSE_DIFFERENT_FILE')
            : this.translate('INPUT_FILE.BUTTON.CHOOSE_FILE');
    }

    /**
     * Re-applies the localised placeholder onto an already-rendered empty-state element.
     * @returns {void}
     */
    #updateEmptyStateText() {
        const emptyState = this.#fileListElement?.querySelector('#emptyState');
        if (!emptyState) { return; }

        emptyState.textContent = this.translate('INPUT_FILE.EMPTY_STATE');
    }

    /**
     * Re-renders every locale-dependent string in place when the culture changes: the
     * upload button label, the empty-state placeholder, and any validation message this
     * component generated itself. Error text supplied by the consuming application via
     * `updateFileState()` is left untouched.
     * @returns {void}
     */
    onLocaleChange() {
        if (!this.isRendered) { return; }

        this.#updateButtonText();
        this.#updateEmptyStateText();

        this.#files.forEach(entry => {
            if (!entry.errorKey) { return; }

            entry.errorMessage = this.#resolveErrorMessage(entry.errorKey);
            this.#fileListElement
                ?.querySelector(`neptune-input-file-item[item-id="${entry.id}"]`)
                ?.setAttribute('error-message', entry.errorMessage);
        });
    }
}

customElements.define('neptune-input-file', InputFile);
export default InputFile;